import type {RefObject} from 'react'
import {useEffect, useRef} from 'react'

/**
 * Dynamically sets `maxHeight` on the element so it never extends below
 * the viewport bottom.  Recalculates on scroll and resize via
 * requestAnimationFrame to avoid layout thrashing.
 */
export function useViewportFitHeight(ref: RefObject<HTMLElement | null>) {
  const rafRef = useRef(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    function measure() {
      rafRef.current = 0
      if (!el) return
      const available = Math.max(0, window.innerHeight - el.getBoundingClientRect().top)
      el.style.maxHeight = `${available}px`
    }

    function scheduleUpdate() {
      if (rafRef.current) return
      rafRef.current = requestAnimationFrame(measure)
    }

    // Initial measurement
    measure()

    // IntersectionObserver cannot be used here: setting maxHeight on the
    // observed element changes its intersection, creating a feedback loop.
    // Passive scroll + rAF is the simplest correct approach for viewport-clamping.
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', scheduleUpdate)
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', scheduleUpdate)

    return () => {
      window.removeEventListener('scroll', scheduleUpdate)
      window.removeEventListener('resize', scheduleUpdate)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      el.style.maxHeight = ''
    }
  }, [ref])
}
